import { API_ENDPOINT, DAILY5_START_DATE, GAME_IDS, GAME_STORAGE_KEYS } from '../config'
import { getPlayerId } from './stats'
import { Result } from '../types'
import apiFetch from '../util/apiFetch'
import calculateScore from '../util/calculateScore'
import getLocalDate from '../util/getLocalDate'
import getPuzzleNumberForDate from '../util/getPuzzleNumberForDate'

export function loadArchiveResultsFromStorage(): Result[] {
	const resultsString = localStorage.getItem(GAME_STORAGE_KEYS.DAILY5_ARCHIVE_RESULTS)
	return resultsString ? JSON.parse(resultsString) : []
}

export type ArchiveResultsByPuzzleNumber = {
	[i: number]: Result
}

export function getArchiveResultsByPuzzleNumber() {
	const results = loadArchiveResultsFromStorage()
	const archiveResultsByPuzzleNumber: ArchiveResultsByPuzzleNumber = {}
	for (const result of results) {
		archiveResultsByPuzzleNumber[result.i] = result
	}
	return archiveResultsByPuzzleNumber
}

function saveArchiveResultsToStorage(archiveResults: Result[]): void {
	const resultsString = JSON.stringify(archiveResults)
	localStorage.setItem(GAME_STORAGE_KEYS.DAILY5_ARCHIVE_RESULTS, resultsString)
}

export function setArchiveResult(newResult: Result) {
	const results = loadArchiveResultsFromStorage()
	const existingResult = results.find(r => r.i === newResult.i)

	if (existingResult) {
		// a result exists for this puzzle already,
		// is the new result's time better than the existing time?
		// if so, update the result in place (note: find() returns a reference)
		// and mark the result to be synced to the server
		//
		// we update the result in place, rather than adding a new result to
		// the archive results in storage just to keep storage use down on the
		// client side - a result that isn't better isn't worth keeping around
		if (newResult.time < existingResult.time) {
			existingResult.time = newResult.time
			existingResult.sync = newResult.sync
		} else {
			// that said if the result isn't better, it would still be useful
			// to know server-side that the player completed an archive puzzle,
			// for analytics purposes
			//
			// this is just best effort post to the server though, if it fails,
			// it won't be re-attempted
			//
			// if we wanted to ensure that the result gets sent to the server
			// we would want to use the sync (result.sync) stuff, as this
			// will handle re-attempts on fail (hence why the code in the
			// previous block _is_ using the sync stuff)
			postArchiveResult(newResult)
		}
	} else {
		results.push(newResult)
	}

	saveArchiveResultsToStorage(results)
	postArchiveResultsToServer()
}

export function archivePuzzleCount() {
	const totalPuzzleCount = getPuzzleNumberForDate(getLocalDate(), DAILY5_START_DATE)
	return totalPuzzleCount - 1 // ignore today's puzzle
}

export function calculateArchiveStats() {
	let completed = 0
	let bestTime: number | null = null
	let bestTimeIndex: number | null = null
	let fiveStarGames = 0

	const archiveCount = archivePuzzleCount()
	const archiveResults = getArchiveResultsByPuzzleNumber()

	for (let index = 1; index <= archiveCount; index++) {
		const archiveResult = archiveResults[index]
		if (archiveResult) {
			if (bestTime === null || archiveResult.time < bestTime) {
				bestTime = archiveResult.time
				bestTimeIndex = archiveResult.i
			}

			const score = calculateScore(archiveResult.time)
			if (score === 5) {
				fiveStarGames++
			}

			completed++
		}
	}

	const completedPercent = Math.floor((completed / archiveCount) * 100)
	const fiveStarPercent = Math.floor((fiveStarGames / archiveCount) * 100)

	// If they've done some, but it got 0% when rounded, show <1% as a kindness
	const completedPercentStr =
		completedPercent === 0 && completed > 0 ? '<1%' : `${completedPercent}%`
	const fiveStarPercentStr =
		fiveStarPercent === 0 && fiveStarGames > 0 ? '<1%' : `${fiveStarPercent}%`

	return {
		completed,
		available: archiveCount,
		completedPercent,
		completedPercentStr,
		bestTime,
		bestTimeIndex,
		fiveStarGames,
		fiveStarPercent,
		fiveStarPercentStr,
	}
}

/**
 * Perform a two-way sync with the server for a player's archive results
 */
export async function syncArchiveResults() {
	// Only sync once per hour
	const lastSynced = localStorage.getItem(GAME_STORAGE_KEYS.DAILY5_ARCHIVE_LAST_SYNCED)
	const now = Date.now()
	if (lastSynced && now < parseInt(lastSynced) + 1000 * 60 * 60) {
		console.log('Synced archive results recently, skipping this time')
		return
	} else {
		console.log('Syncing archive results')
		localStorage.setItem(GAME_STORAGE_KEYS.DAILY5_ARCHIVE_LAST_SYNCED, `${now}`)
	}

	// First, pull and merge in server results, if we haven't in the last hour
	const serverResults = await getArchiveResultsFromServer()
	const localResults = loadArchiveResultsFromStorage()

	serverResults.forEach(serverResult => {
		const localResult = localResults.find(r => r.i === serverResult.i)
		if (!localResult) {
			// Add the server result if no local result exists for the puzzle
			localResults.push(serverResult)
		} else if (serverResult.time < localResult.time) {
			// Or overwrite the local result if the server result is better
			localResult.time = serverResult.time
		}
	})

	// Just in case, check for any local results that do not exist on the
	// server and mark them for syncing
	localResults.forEach(localResult => {
		const serverResult = serverResults.find(r => r.i === localResult.i)
		if (!serverResult) {
			localResult.sync === false
		}
	})

	// Then update the results in storage
	saveArchiveResultsToStorage(localResults)

	// Then post any unsynced results to the server
	postArchiveResultsToServer()
}

async function getArchiveResultsFromServer(): Promise<Result[]> {
	try {
		const response = await fetchArchiveResults()
		if ('results' in response) {
			return response.results
		}
		throw new Error(response.message ?? 'Failed to fetch archive results')
	} catch (e) {
		console.error(e)
		return []
	}
}

export async function postArchiveResultsToServer() {
	const results = loadArchiveResultsFromStorage()
	const resultToSync = results.find(r => r.sync === false)
	if (!resultToSync) return

	try {
		const { sync: _, ...result } = resultToSync
		const response = await postArchiveResult(result)
		if (response.message) {
			// A successful response is a blank object, something went wrong
			throw new Error(response.message)
		}

		// Successfully synced
		delete resultToSync.sync
		saveArchiveResultsToStorage(results)

		// After a few seconds, see if we’ve got any other results that should
		// be posted to the server
		setTimeout(postArchiveResultsToServer, 5000)
	} catch (e) {
		console.error(e)
	}
}

// prettier-ignore
type FetchArchiveResultsResponse = {
	results: Result[]
} | {
	code?: string
	message?: string
}

async function fetchArchiveResults(): Promise<FetchArchiveResultsResponse> {
	const url = `${API_ENDPOINT}/archiveResults?pid=${getPlayerId()}`
	return await apiFetch(url, 'GET')
}

// A successful server response is a blank object, anything else is an error
type PostArchiveResultResponse = {
	code?: string
	message?: string
}

async function postArchiveResult(result: Result): Promise<PostArchiveResultResponse> {
	const url = `${API_ENDPOINT}/postArchiveResult`
	const body = {
		result: {
			i: result.i,
			time: result.time,
		},
		gameId: GAME_IDS.DAILY5,
		pid: getPlayerId(),
	}
	return await apiFetch(url, 'POST', body)
}
